/**
 * Generic RPC Provider
 * Provider genérico para redes que no tienen Alchemy
 * Obtiene transacciones directamente del RPC usando ethers.js
 */

import { ethers } from 'ethers';
import { Transaction } from '@/types/transaction.types';
import { BaseTransactionProvider } from './base-provider';

export class GenericRPCProvider extends BaseTransactionProvider {
  private provider: ethers.JsonRpcProvider;

  constructor(network: string, rpcUrl: string) {
    super(network);
    this.provider = new ethers.JsonRpcProvider(rpcUrl);
  }

  /**
   * Sin indexador no hay forma confiable de listar el historial completo.
   */
  async getTransactions(_address: string, _fromBlock?: number): Promise<Transaction[]> {
    return [];
  }

  /**
   * Obtiene una transacción específica por hash
   */
  async getTransaction(txHash: string): Promise<Transaction | null> {
    try {
      const tx = await this.provider.getTransaction(txHash);
      if (!tx) return null;

      const receipt = await this.provider.getTransactionReceipt(txHash);
      const block = tx.blockNumber ? await this.provider.getBlock(tx.blockNumber) : null;

      return {
        hash: tx.hash,
        from: tx.from,
        to: tx.to || '',
        value: tx.value.toString(),
        blockNumber: tx.blockNumber || 0,
        timestamp: block?.timestamp || Math.floor(Date.now() / 1000),
        status: receipt?.status === 1 ? 'confirmed' : receipt?.status === 0 ? 'failed' : 'pending',
        type: 'send',
        network: this.network,
        gasUsed: receipt?.gasUsed.toString(),
        gasPrice: tx.gasPrice?.toString(),
        source: 'rpc',
      };
    } catch (error) {
      console.error(`[GenericRPC] Error obteniendo transacción ${txHash}:`, error);
      return null;
    }
  }

  /**
   * Obtiene transacciones pendientes
   */
  async getPendingTransactions(_address: string): Promise<Transaction[]> {
    // Los RPCs genéricos no tienen forma de obtener transacciones pendientes
    // Solo se pueden obtener las que están en el mempool local
    return [];
  }

  /**
   * Sin indexador no hay forma confiable de listar transferencias de tokens.
   */
  async getTokenTransfers(_address: string, _contractAddress?: string): Promise<Transaction[]> {
    return [];
  }

  /**
   * Obtiene detalles de una transacción (implementación requerida por BaseTransactionProvider)
   */
  async getTransactionDetails(txHash: string): Promise<Transaction | null> {
    return this.getTransaction(txHash);
  }

  /**
   * Verifica el estado de una transacción
   */
  async getTransactionStatus(txHash: string): Promise<'pending' | 'confirmed' | 'failed'> {
    try {
      const receipt = await this.provider.getTransactionReceipt(txHash);
      
      if (!receipt) {
        return 'pending';
      }

      return receipt.status === 1 ? 'confirmed' : 'failed';
    } catch (error) {
      console.error(`[GenericRPC] Error verificando estado de ${txHash}:`, error);
      return 'pending';
    }
  }
}
